import './InternalSecondaryMobile.css';
// src/MaintenanceAffiliatePayouts.js
//
// Affiliate commission tracker (L3 only — CAT_FINANCE).
//
// Commissions accrue on work billed to the condo CORPORATION only. Resident
// in-suite invoices are billed to the individual and never enter this ledger.
//
// The "owed" figure is money that has actually landed in our account: a
// percentage of the pre-HST subtotal of paid corp invoices. The smaller
// figure beneath it is the forecast from invoices we've sent but haven't been
// paid for — deliberately de-emphasised, because paying against it means
// paying commission on money we don't have yet.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import {
  FaBuilding, FaCaretDown, FaCaretUp, FaCheck, FaChevronRight,
  FaExclamationTriangle, FaExternalLinkAlt, FaFileCsv, FaHistory, FaPlus,
  FaSyncAlt, FaTimes, FaTrash, FaUserTie,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { mergeAffiliatePipeline, readAffiliateJson, readAffiliateForecast } from './affiliateReadData';

const C = {
  navy: '#1F3A68',
  ink: '#1a2236',
  inkSoft: '#4a5468',
  muted: '#7a8499',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  blue: '#2563eb',
  blueSoft: '#e8f0fe',
  amber: '#C68A1C',
  amberSoft: '#fdf3df',
  red: '#B62525',
  redSoft: '#fbe2e2',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

function authHeaders() {
  const token = localStorage.getItem('managerToken') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
}

function fmtMoney(v, decimals = 2) {
  const n = Number(v || 0);
  return n.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ---------- atoms ----------------------------------------------------------

function PrimaryButton({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? '#9CA8C3' : '#0E8C46',
        color: '#fff',
        border: 'none',
        padding: '10px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: '#fff',
        color: C.ink,
        border: `1px solid ${C.line}`,
        padding: '10px 16px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Modal({ open, onClose, title, subtitle, width = 680, children, footer }) {
  return (
    <BootstrapModal
      show={open}
      onHide={onClose}
      centered
      fullscreen="sm-down"
      className="affiliate-mobile-dialog"
      aria-label={title}
      style={{ '--bs-modal-width': `${width}px`, fontFamily: sharedFont }}
    >
      <BootstrapModal.Header closeButton style={{ padding: '18px 22px' }}>
        <div>
          <BootstrapModal.Title as="h3" style={{ fontSize: 17, color: C.ink, fontWeight: 700 }}>{title}</BootstrapModal.Title>
          {subtitle && <div style={{ marginTop: 4, fontSize: 12.5, color: C.muted }}>{subtitle}</div>}
        </div>
      </BootstrapModal.Header>
      <BootstrapModal.Body style={{ padding: 22 }}>{children}</BootstrapModal.Body>
      {footer && <BootstrapModal.Footer style={{ padding: '14px 22px' }}>{footer}</BootstrapModal.Footer>}
    </BootstrapModal>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>
        {label}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  fontSize: 13.5,
  fontFamily: sharedFont,
  color: C.ink,
  boxSizing: 'border-box',
};

// Type-to-filter building picker. Purpose-built rather than reusing
// Components/Shared/SearchableSelect, which is a free-text combobox — it
// reports whatever you type and offers to accept unmatched input, so a typo
// could be saved as a building that doesn't exist. Here only a real
// buildingId can ever leave the component.
function BuildingPicker({ buildings, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef(null);

  const selected = buildings.find((b) => b.buildingId === value) || null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return buildings.slice(0, 60);
    // Prefer buildings whose name starts with the query — typing "220" should
    // surface 220 Forum before 1220 Something.
    const starts = [];
    const contains = [];
    for (const b of buildings) {
      const n = (b.name || '').toLowerCase();
      if (n.startsWith(q)) starts.push(b);
      else if (n.includes(q)) contains.push(b);
    }
    return [...starts, ...contains].slice(0, 60);
  }, [buildings, query]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (b) => {
    onChange(b.buildingId);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => Math.min(c + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && matches[cursor]) {
        e.preventDefault();
        pick(matches[cursor]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        style={{
          ...inputStyle,
          color: selected && !open ? C.ink : undefined,
          fontWeight: selected && !open ? 600 : 400,
        }}
        value={open ? query : (selected ? selected.name : '')}
        placeholder={selected ? selected.name : 'Type to search buildings…'}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => onChange('')}
          title="Clear"
          style={{
            position: 'absolute', right: 8, top: 8, background: 'transparent',
            border: 'none', cursor: 'pointer', color: C.muted, fontSize: 12,
          }}
        >
          <FaTimes />
        </button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: '#fff', border: `1px solid ${C.line}`, borderRadius: 8,
          boxShadow: '0 12px 28px rgba(15,23,42,.16)', zIndex: 1100,
          maxHeight: 260, overflowY: 'auto',
        }}>
          {matches.length === 0 && (
            <div style={{ padding: '11px 13px', fontSize: 13, color: C.muted }}>
              No building matches “{query}”.
            </div>
          )}
          {matches.map((b, i) => (
            <button
              key={b.buildingId}
              type="button"
              onMouseEnter={() => setCursor(i)}
              onClick={() => pick(b)}
              style={{
                width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                padding: '9px 13px', fontSize: 13.5, fontFamily: sharedFont,
                background: i === cursor ? C.blueSoft : 'transparent',
                color: C.ink, display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: 8,
              }}
            >
              <span>
                {b.name}
                {b.covers?.length > 1 && (
                  <span style={{ display: 'block', fontSize: 10.5, color: C.muted }}>
                    {b.covers.map((c) => c.name).join(' · ')}
                  </span>
                )}
                {b.duplicateOf && (
                  <span style={{ display: 'block', fontSize: 10.5, color: C.red }}>
                    duplicate — the linked copy is id {b.duplicateOf.buildingId}
                  </span>
                )}
              </span>
              {!b.linked && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: C.amber,
                  background: C.amberSoft, padding: '2px 6px', borderRadius: 5,
                  whiteSpace: 'nowrap',
                }}>
                  NO JOBBER CLIENT
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Just a speed bump so nobody wipes a test run with a stray click.
function ResetModal({ open, onClose, onDone }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setErr('');
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/reset`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      onDone(`Wiped ${body.total} row${body.total === 1 ? '' : 's'} — clean slate.`);
      onClose();
    } catch (e) {
      setErr(e.message || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete all affiliate data"
      subtitle="For starting a test run from scratch."
      width={460}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <button
            onClick={submit}
            disabled={saving || !password}
            style={{
              background: C.red, color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 15px', fontSize: 13.5, fontWeight: 600,
              fontFamily: sharedFont, opacity: password ? 1 : 0.5,
              cursor: saving || !password ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? <Working label="Deleting…" /> : 'Delete everything'}
          </button>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      <div style={{ fontSize: 13, color: C.inkSoft, marginBottom: 12 }}>
        Clears every affiliate, agreement, earning and payout. Buildings and
        Jobber are untouched, so a sync afterwards rebuilds the earnings —
        but recorded payouts only live here and can't be recovered.
      </div>
      <Field label="Password">
        <input style={inputStyle} type="password" value={password} autoFocus
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && password) submit(); }} />
      </Field>
    </Modal>
  );
}

// A one-off deal on a single invoice: a different rate, a flat fee, or nothing
// at all. Rare, so it lives behind the rate on each invoice row rather than
// anywhere prominent.
function DealModal({ open, affiliateId, invoice, onClose, onSaved }) {
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const current = invoice?.override || null;

  useEffect(() => {
    if (!open) return;
    setMode(current?.mode || 'percent');
    setValue(current ? String(current.value) : String(invoice?.agreedPercent ?? ''));
    setNote(current?.note || '');
    setErr('');
  }, [open, current, invoice]);

  const send = async (clear) => {
    setSaving(true);
    setErr('');
    try {
      const url = clear
        ? `${API_BASE_URL}/api/affiliates/${affiliateId}/overrides/${current.id}`
        : `${API_BASE_URL}/api/affiliates/${affiliateId}/overrides`;
      const res = await fetch(url, {
        method: clear ? 'DELETE' : 'POST',
        headers: authHeaders(),
        body: clear ? undefined : JSON.stringify({
          invoiceId: invoice.invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          buildingId: invoice.buildingId || '',
          mode,
          value: Number(value || 0),
          note,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      const moved = body.adjusted || 0;
      onSaved(
        (clear ? `#${invoice.invoiceNumber} back on the agreed rate.`
          : `#${invoice.invoiceNumber} set to `
            + (mode === 'percent' ? `${Number(value)}%` : fmtMoney(Number(value))))
        + (moved
          ? ` Owed ${moved > 0 ? 'increased' : 'reduced'} by ${fmtMoney(Math.abs(moved))}.`
          : ''),
      );
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not save the deal.');
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;
  // The building view calls it `base`, the ledger `earnedBase`; either way it's
  // the pre-HST amount this row is priced off.
  const base = invoice.base ?? invoice.earnedBase ?? 0;
  const invoiceBase = invoice.invoiceBase || base;
  const partial = invoiceBase > 0 && base < invoiceBase - 0.005;
  const preview = mode === 'percent'
    ? base * (Number(value || 0) / 100)
    : (invoiceBase > 0 ? Number(value || 0) * base / invoiceBase : 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Custom deal — invoice #${invoice.invoiceNumber}`}
      subtitle={`Normally ${invoice.agreedPercent}% of ${fmtMoney(base)}.`}
      width={490}
      footer={(
        <>
          {current && (
            <button
              onClick={() => send(true)}
              disabled={saving}
              style={{
                background: 'transparent', border: 'none', color: C.red,
                fontSize: 12.5, fontWeight: 600, fontFamily: sharedFont,
                cursor: 'pointer', marginRight: 'auto',
              }}
            >
              Remove deal
            </button>
          )}
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={() => send(false)} disabled={saving || value === ''}>
            {saving ? <Working label="Saving…" /> : 'Save deal'}
          </PrimaryButton>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Deal type">
          <select style={inputStyle} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="percent">A different percentage</option>
            <option value="amount">A flat dollar amount</option>
          </select>
        </Field>
        <Field label={mode === 'percent' ? 'Percent' : 'Amount'}
          hint={mode === 'percent' ? '0 for nothing owed' : 'For the whole invoice'}>
          <input style={inputStyle} value={value} inputMode="decimal"
            onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
      <Field label="Why" hint="Worth a line — this overrides the agreement.">
        <input style={inputStyle} value={note} placeholder="e.g. affiliate waived their cut on this job"
          onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div style={{
        background: C.bg, borderRadius: 9, padding: '10px 13px', fontSize: 13,
        color: C.inkSoft,
      }}>
        Commission on this invoice becomes{' '}
        <strong style={{ color: C.ink }}>{fmtMoney(preview)}</strong>
        {' '}instead of {fmtMoney(base * (invoice.agreedPercent || 0) / 100)}.
        {mode === 'amount' && partial && (
          <div style={{ marginTop: 6, fontSize: 12 }}>
            Only {fmtMoney(base)} of this {fmtMoney(invoiceBase)} invoice has been
            paid so far, so the flat fee is prorated. The rest follows when the
            balance clears.
          </div>
        )}
        {invoice.kind !== 'pending' && (
          <div style={{ marginTop: 6, fontSize: 12 }}>
            This invoice has already been paid by the corporation, so the
            difference is written as an adjustment. Nothing already paid out to
            the affiliate is rewritten — if they've been overpaid, the balance
            carries against their next payout.
          </div>
        )}
      </div>
    </Modal>
  );
}

// Correcting a start date that was left as today. Moving it earlier is the
// normal case and just widens the window; the server refuses to move it past
// commissions that already exist.
function StartDateModal({ open, agreement, onClose, onSaved }) {
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setStartDate((agreement?.startDate || '').slice(0, 10));
    setErr('');
  }, [open, agreement]);

  const original = (agreement?.startDate || '').slice(0, 10);
  const earlier = startDate && startDate < original;
  const later = startDate && startDate > original;

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/affiliates/agreements/${agreement.id}/start-date`,
        { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ startDate }) },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      const found = body.synced?.created || 0;
      onSaved(body.unchanged
        ? 'Start date unchanged.'
        : `Start date moved to ${fmtDate(body.startDate)}.`
          + (found ? ` Picked up ${found} earlier commission${found === 1 ? '' : 's'}.`
            : ' No earlier commissions found.'));
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not change the start date.');
    } finally {
      setSaving(false);
    }
  };

  if (!agreement) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Start date — ${agreement.buildingName}`}
      subtitle={`Currently earning from ${fmtDate(original)} at ${agreement.percent}%.`}
      width={480}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || !startDate}>
            {saving ? <Working label="Updating & syncing…" /> : 'Save start date'}
          </PrimaryButton>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      <Field label="Earn commission on invoices issued from">
        <input style={inputStyle} type="date" value={startDate}
          onChange={(e) => setStartDate(e.target.value)} />
      </Field>
      {earlier && (
        <Banner tone="blue">
          Widening the window back to {fmtDate(startDate)}. Everything already
          earned or paid stays exactly as it is — this only lets older invoices
          count, and they'll be pulled in as soon as you save.
        </Banner>
      )}
      {later && (
        <Banner tone="amber">
          Narrowing the window. This is only allowed if no commission has been
          earned before {fmtDate(startDate)} — if any has, you'll be told which
          invoices rather than silently losing them.
        </Banner>
      )}
    </Modal>
  );
}

// Column sorting for a table. Each column supplies both how to read its value
// and which way it should open, because these tables mix text, dates and money
// and the useful first click differs: names read A-Z, money and dates read
// biggest or most recent first.
function useSort(rows, cols, initialKey) {
  const [sort, setSort] = useState(
    () => ({ key: initialKey, dir: cols[initialKey]?.dir || 'desc' }),
  );

  const toggle = useCallback((key) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: cols[key]?.dir || 'desc' }));
  }, [cols]);

  const sorted = useMemo(() => {
    const read = cols[sort.key]?.get;
    if (!read) return rows;
    const mult = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = read(a);
      const y = read(b);
      if (x < y) return -mult;
      if (x > y) return mult;
      return 0;
    });
  }, [rows, sort, cols]);

  return { sorted, sort, toggle };
}

function SortHeader({ label, sortKey, sort, onSort, align = 'left', style }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        ...th, textAlign: align, cursor: 'pointer', userSelect: 'none',
        whiteSpace: 'nowrap', color: active ? C.navy : undefined, ...style,
      }}
      title={`Sort by ${label.toLowerCase()}`}
    >
      {label}
      <span style={{
        marginLeft: 5, fontSize: 8,
        // Held at full opacity only on the active column, but always present so
        // the header row doesn't reflow as you click between columns.
        opacity: active ? 1 : 0.25,
      }}>
        {active && sort.dir === 'asc' ? <FaCaretUp /> : <FaCaretDown />}
      </span>
    </th>
  );
}

function Spinner({ size = 12, color = 'currentColor' }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block', width: size, height: size,
        border: `2px solid ${color}`, borderTopColor: 'transparent',
        borderRadius: '50%', animation: 'spin .7s linear infinite',
        verticalAlign: '-2px',
      }}
    />
  );
}

// Anything that waits on Jobber can take seconds, and a dead-looking screen
// reads as broken rather than busy.
function Working({ label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <Spinner /> {label}
    </span>
  );
}

const STATUS_LABEL = {
  paid: 'Paid',
  past_due: 'Past due',
  awaiting_payment: 'Awaiting payment',
  sent_not_due: 'Sent',
  bad_debt: 'Bad debt',
};

const BUILDING_INVOICE_COLS = {
  invoice: { get: (i) => Number(i.invoiceNumber) || 0, dir: 'desc' },
  issued: { get: (i) => i.invoiceDate || '', dir: 'desc' },
  // Sorts on the label actually in the cell, so clicking Status groups what
  // the eye already groups rather than the raw field behind it.
  status: {
    get: (i) => (i.kind === 'clawback'
      ? (i.adjustmentType || 'REVERSAL')
      : (STATUS_LABEL[i.status] || i.status || '')).toLowerCase(),
    dir: 'asc',
  },
  base: { get: (i) => i.base || 0, dir: 'desc' },
  rate: { get: (i) => i.percent || 0, dir: 'desc' },
  amount: { get: (i) => i.amount || 0, dir: 'desc' },
};

const compactTh = { padding: '6px 10px' };

// One attached building, expandable into the invoices behind its numbers.
// Invoices the client has already paid are faded: the commission is banked and
// needs no attention, whereas an unpaid invoice is money still to chase.
function BuildingRow({ agreement, rollup, onDetach, onEditStart, onEditDeal, busy }) {
  const [open, setOpen] = useState(false);
  const allInvoices = rollup?.invoices || [];
  const { sorted: invoices, sort, toggle } = useSort(
    allInvoices, BUILDING_INVOICE_COLS, 'issued',
  );
  const earned = rollup?.earned || 0;
  const owed = rollup?.owed || 0;
  const pending = rollup?.pending || 0;

  return (
    <>
      <tr
        onClick={() => invoices.length && setOpen((v) => !v)}
        style={{ cursor: invoices.length ? 'pointer' : 'default' }}
      >
        <td style={td}>
          <span style={{
            display: 'inline-block', width: 14, color: C.muted, fontSize: 10,
            transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s',
          }}>
            {invoices.length ? <FaChevronRight /> : null}
          </span>
          {agreement.buildingName}
          {agreement.covers?.length > 1 && (
            <div style={{ fontSize: 11, color: C.muted, marginLeft: 14, marginTop: 2 }}>
              {agreement.covers.join(' · ')}
            </div>
          )}
          {/* Only reachable if two already-attached buildings become one
              client in Jobber after the fact, since attaching now groups
              them. Left in because that merge would otherwise silently
              double the commission. */}
          {agreement.sharesClientWith?.length > 0 && (
            <div style={{ fontSize: 11, color: C.red, marginLeft: 14, marginTop: 2 }}>
              Now shares a Jobber client with {agreement.sharesClientWith.join(', ')} —
              {agreement.isBookingBuilding
                ? ' counted here once for all of them'
                : ` counted under ${agreement.booksTo}, so detach this one`}
              {agreement.rateConflict && ' · rates disagree'}
            </div>
          )}
        </td>
        <td style={td}><strong>{agreement.percent}%</strong></td>
        <td style={td}>
          <button
            onClick={(e) => { e.stopPropagation(); onEditStart(); }}
            title="Change the date commissions start from"
            style={{
              background: 'transparent', border: 'none', padding: 0,
              cursor: 'pointer', fontFamily: sharedFont, fontSize: 12.5,
              color: C.muted, textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            }}
          >
            {fmtDate(agreement.startDate)}
          </button>
        </td>
        <td style={{ ...td, textAlign: 'right' }}>{fmtMoney(earned)}</td>
        <td style={{ ...td, textAlign: 'right' }}>
          <div style={{ fontWeight: 600 }}>{fmtMoney(owed)}</div>
          {pending > 0 && (
            <div style={{ fontSize: 11, color: C.muted }}>
              +{fmtMoney(pending)} pending
            </div>
          )}
        </td>
        <td style={{ ...td, textAlign: 'right' }}>
          <button
            onClick={(e) => { e.stopPropagation(); onDetach(); }}
            disabled={busy}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.red, fontSize: 12, fontWeight: 600,
            }}
          >
            {busy ? <Spinner color={C.red} /> : 'Detach'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ padding: 0, background: C.bg }}>
            <div style={{ padding: '10px 14px 14px 28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <SortHeader label="Invoice" sortKey="invoice" sort={sort}
                      onSort={toggle} style={compactTh} />
                    <SortHeader label="Issued" sortKey="issued" sort={sort}
                      onSort={toggle} style={compactTh} />
                    <SortHeader label="Status" sortKey="status" sort={sort}
                      onSort={toggle} style={compactTh} />
                    <SortHeader label="Commissionable" sortKey="base" sort={sort}
                      onSort={toggle} align="right" style={compactTh} />
                    <SortHeader label="Rate" sortKey="rate" sort={sort}
                      onSort={toggle} align="right" style={compactTh} />
                    <SortHeader label="Commission" sortKey="amount" sort={sort}
                      onSort={toggle} align="right" style={compactTh} />
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => {
                    const isPending = i.kind === 'pending';
                    const isClawback = i.kind === 'clawback';
                    return (
                      <tr
                        key={`${i.kind}-${i.invoiceNumber}-${i.invoiceDate}`}
                        style={{ opacity: isPending ? 1 : 0.55 }}
                      >
                        <td style={{ ...td, padding: '7px 10px', fontSize: 12.5 }}>
                          <InvoiceLink number={i.invoiceNumber} url={i.jobberUrl} />
                          {i.settled && (
                            <span style={{
                              marginLeft: 6, fontSize: 9.5, fontWeight: 700,
                              color: C.green, background: C.greenSoft,
                              padding: '1px 5px', borderRadius: 4,
                            }}>
                              PAID OUT
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, padding: '7px 10px', fontSize: 12.5, color: C.muted }}>
                          {fmtDate(i.invoiceDate)}
                        </td>
                        <td style={{ ...td, padding: '7px 10px' }}>
                          <span style={{
                            fontSize: 10.5, fontWeight: 700, borderRadius: 5,
                            padding: '2px 7px',
                            color: isPending ? C.amber : C.green,
                            background: isPending ? C.amberSoft : C.greenSoft,
                          }}>
                            {isClawback
                              ? (i.adjustmentType || 'REVERSAL')
                              : (STATUS_LABEL[i.status] || i.status)}
                          </span>
                        </td>
                        <td style={{ ...td, padding: '7px 10px', textAlign: 'right', fontSize: 12.5 }}>
                          {fmtMoney(i.base)}
                        </td>
                        <td style={{ ...td, padding: '7px 10px', textAlign: 'right', fontSize: 12.5 }}>
                          {i.kind === 'adjustment' ? '—' : (
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditDeal(i); }}
                              title={i.override
                                ? `Custom deal${i.override.note ? `: ${i.override.note}` : ''}`
                                : 'Set a custom deal for this invoice'}
                              style={{
                                background: 'transparent', border: 'none', padding: 0,
                                cursor: 'pointer', fontFamily: sharedFont,
                                fontSize: 12.5,
                                color: i.override ? C.amber : C.inkSoft,
                                fontWeight: i.override ? 700 : 400,
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                              }}
                            >
                              {i.percent}%{i.override ? '*' : ''}
                            </button>
                          )}
                        </td>
                        <td style={{
                          ...td, padding: '7px 10px', textAlign: 'right',
                          fontWeight: 600,
                          color: i.amount < 0 ? C.red : (isPending ? C.amber : C.ink),
                        }}>
                          {fmtMoney(i.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>
                Faded rows are invoices the corporation has already paid, so
                that commission is banked. Highlighted rows are still
                outstanding — nothing is owed on them until the money arrives.
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Pill({ label, value, sub, tone = 'navy' }) {
  const tones = {
    navy: { bg: C.card, fg: C.navy },
    green: { bg: C.greenSoft, fg: C.green },
    amber: { bg: C.amberSoft, fg: C.amber },
  };
  const t = tones[tone] || tones.navy;
  return (
    <div role="group" aria-label={label} style={{
      background: t.bg, border: `1px solid ${C.line}`, borderRadius: 12,
      padding: '14px 18px', minWidth: 168, boxShadow: C.shadow,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: C.muted,
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: t.fg, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Invoice numbers open the invoice in Jobber. Falls back to plain text when we
// can't work out a link, so a row is never silently unclickable-looking.
function InvoiceLink({ number, url, style }) {
  const label = `#${number}`;
  if (!url) return <span style={style}>{label}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open this invoice in Jobber"
      onClick={(e) => e.stopPropagation()}
      style={{
        color: C.navy, fontWeight: 600, textDecoration: 'none',
        borderBottom: `1px solid ${C.line}`, ...style,
      }}
    >
      {label}
      <FaExternalLinkAlt size={8} style={{ marginLeft: 4, opacity: 0.55 }} />
    </a>
  );
}

function Banner({ tone = 'amber', children }) {
  const tones = {
    amber: { bg: C.amberSoft, fg: C.amber },
    red: { bg: C.redSoft, fg: C.red },
    blue: { bg: C.blueSoft, fg: C.navy },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div style={{
      background: t.bg, color: t.fg, borderRadius: 10, padding: '10px 14px',
      fontSize: 12.5, display: 'flex', gap: 9, alignItems: 'flex-start',
      marginBottom: 14, lineHeight: 1.45,
    }}>
      <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
      <div>{children}</div>
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11,
  textTransform: 'uppercase', letterSpacing: 0.4, color: C.muted,
  fontWeight: 700, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap',
};
const td = {
  padding: '12px', fontSize: 13.5, color: C.ink,
  borderBottom: `1px solid ${C.lineSoft}`, verticalAlign: 'middle',
};

// ---------- add / edit affiliate ------------------------------------------

function AffiliateModal({ open, editing, types, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', type: '', company: '', email: '', phone: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setErr('');
    setForm({
      name: editing?.name || '',
      type: editing?.type || '',
      company: editing?.company || '',
      email: editing?.email || '',
      phone: editing?.phone || '',
      notes: editing?.notes || '',
    });
  }, [open, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) {
      setErr('A name is required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const isEdit = Boolean(editing?.id);
      const res = await fetch(
        isEdit ? `${API_BASE_URL}/api/affiliates/${editing.id}` : `${API_BASE_URL}/api/affiliates`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify(form),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      onSaved(isEdit ? 'Affiliate updated.' : 'Affiliate added.');
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing?.id ? 'Edit affiliate' : 'Add affiliate'}
      subtitle="Attach buildings and set their percentage after saving."
      width={560}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving}>
            {saving ? <Working label="Saving…" /> : 'Save'}
          </PrimaryButton>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      <Field label="Name">
        <input style={inputStyle} value={form.name} onChange={set('name')} placeholder="Jane Doe" />
      </Field>
      <Field label="Type" hint="Their role at the building. Doesn't affect the commission.">
        <select style={inputStyle} value={form.type} onChange={set('type')}>
          <option value="">Select a type…</option>
          {(types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Company">
        <input style={inputStyle} value={form.company} onChange={set('company')} placeholder="Optional" />
      </Field>
      <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Email">
          <input style={inputStyle} value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Phone">
          <input style={inputStyle} value={form.phone} onChange={set('phone')} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
          value={form.notes} onChange={set('notes')} />
      </Field>
    </Modal>
  );
}

// ---------- attach building / change percent ------------------------------

function AgreementModal({ open, affiliate, buildings, buildingsLoading, buildingsError, onLoadBuildings, onClose, onSaved }) {
  const [buildingId, setBuildingId] = useState('');
  const [percent, setPercent] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setBuildingId('');
    setPercent('');
    setStartDate(todayIso());
    setErr('');
    onLoadBuildings();
  }, [open, onLoadBuildings]);

  const selected = buildings.find((b) => b.buildingId === buildingId);
  const existing = (affiliate?.agreements || []).find(
    (a) => a.buildingId === buildingId && a.current,
  );

  const submit = async () => {
    const pct = Number(percent);
    if (!buildingId) { setErr('Pick a building.'); return; }
    if (!(pct > 0 && pct <= 100)) { setErr('Percent must be between 0 and 100.'); return; }
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/${affiliate.affiliate.id}/agreements`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ buildingId, percent: pct, startDate }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      // The server syncs this building's client inline, so say what it found
      // rather than making the next screen the first hint.
      const found = body.synced?.created || 0;
      const base = existing ? 'Rate updated.' : 'Building attached.';
      const picked = body.synced?.error
        ? ' Commissions could not be pulled — run Sync from Jobber.'
        : (found
          ? ` Picked up ${found} commission${found === 1 ? '' : 's'} straight away.`
          : ' No commissions to pick up yet.');
      onSaved(body.warning || base + picked);
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Attach building"
      subtitle="Commissions accrue on corporation-billed invoices only."
      width={560}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || buildingsLoading || !!buildingsError}>
            {saving ? <Working label="Attaching & syncing…" /> : 'Save'}
          </PrimaryButton>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      {existing && (
        <Banner tone="blue">
          {selected?.name} is already at {existing.percent}%. Saving a different
          percent starts a new rate on the date below and leaves everything
          earned before it at {existing.percent}%.
        </Banner>
      )}
      {selected?.duplicateOf ? (
        <Banner tone="red">
          This looks like a second copy of {selected.duplicateOf.name}, and it's
          the copy with no Jobber client. The linked copy is building
          id {selected.duplicateOf.buildingId} — attach that one instead, or this
          agreement will sit at $0 forever.
        </Banner>
      ) : selected && !selected.linked && (
        <Banner tone="amber">
          {selected.name} has no Jobber client linked, so nothing will accrue
          until one is mapped. You can still attach it now.
        </Banner>
      )}
      {buildingsLoading && <div role="status" style={{ padding: '10px 0', color: C.muted }}><Working label="Loading buildings…" /></div>}
      {buildingsError && <Banner tone="red">{buildingsError} <button type="button" onClick={() => onLoadBuildings(true)}>Try again</button></Banner>}
      {!buildingsLoading && !buildingsError && (
        <Field label="Building" hint={`${buildings.length} buildings — start typing to narrow it down.`}>
          <BuildingPicker buildings={buildings} value={buildingId} onChange={setBuildingId} />
        </Field>
      )}
      <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Percent" hint="Of the pre-HST subtotal.">
          <input style={inputStyle} value={percent} inputMode="decimal"
            onChange={(e) => setPercent(e.target.value)} placeholder="8" />
        </Field>
        <Field label="Starts" hint="Rate applies to invoices issued on or after this date.">
          <input style={inputStyle} type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------- record a payout -----------------------------------------------

function PayoutModal({ open, affiliate, accountLoading, accountError, onReload, onClose, onSaved, onEditDeal }) {
  const owed = affiliate?.totals?.owed || 0;
  const earnings = useMemo(() => affiliate?.earnings || [], [affiliate?.earnings]);

  // What a payout is almost always actually for: commissions the corporation
  // has already paid us and we haven't passed on yet.
  const unsettled = useMemo(
    () => (earnings || []).filter((e) => e.outstanding > 0.005),
    [earnings],
  );
  // Clawbacks aren't optional. A negative row reduces what's owed, so leaving
  // it out of an explicit allocation would quietly overpay them.
  const clawbacks = useMemo(
    () => (earnings || []).filter((e) => e.outstanding < -0.005),
    [earnings],
  );

  const [picked, setPicked] = useState({});
  const [amount, setAmount] = useState('');
  const [amountEdited, setAmountEdited] = useState(false);
  const [paidDate, setPaidDate] = useState(todayIso());
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    const all = {};
    unsettled.forEach((e) => { all[e.id] = true; });
    setPicked(all);
    setAmountEdited(false);
    setPaidDate(todayIso());
    setMethod('');
    setReference('');
    setNotes('');
    setErr('');
  }, [open, unsettled]);

  const selected = useMemo(
    () => unsettled.filter((e) => picked[e.id]),
    [unsettled, picked],
  );
  const clawbackTotal = useMemo(
    () => clawbacks.reduce((s, e) => s + e.outstanding, 0),
    [clawbacks],
  );
  // What the ticked invoices come to once clawbacks net off.
  const selectedTotal = Math.round(
    (selected.reduce((s, e) => s + e.outstanding, 0) + clawbackTotal) * 100,
  ) / 100;

  // The amount tracks the ticks until you type over it, so the common case
  // needs no arithmetic and the odd rounded cheque is still possible.
  useEffect(() => {
    if (!open || amountEdited) return;
    setAmount(selectedTotal > 0 ? selectedTotal.toFixed(2) : '');
  }, [open, amountEdited, selectedTotal]);

  const byBuilding = useMemo(() => {
    const m = new Map();
    unsettled.forEach((e) => {
      const k = e.buildingName || '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return [...m.entries()];
  }, [unsettled]);

  const amt = Number(amount || 0);
  const short = selectedTotal - amt;
  const over = amt - selectedTotal;

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const toggleBuilding = (items, on) => setPicked((p) => {
    const next = { ...p };
    items.forEach((e) => { next[e.id] = on; });
    return next;
  });

  const submit = async () => {
    if (accountLoading || accountError) return;
    if (!(amt > 0)) { setErr('Enter an amount greater than zero.'); return; }
    if (!selected.length && !clawbacks.length) {
      setErr('Tick at least one invoice this payout covers.');
      return;
    }
    if (short > 0.005) {
      setErr(`The ticked invoices come to ${fmtMoney(selectedTotal)}, more than `
             + `the ${fmtMoney(amt)} you're recording. Untick some or raise the amount.`);
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const allocations = [
        ...clawbacks.map((e) => ({ earningId: e.id, amount: e.outstanding })),
        ...selected.map((e) => ({ earningId: e.id, amount: e.outstanding })),
      ];
      const res = await fetch(`${API_BASE_URL}/api/affiliates/${affiliate.affiliate.id}/payouts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amount: amt, paidDate, method, reference, notes, allocations }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.msg || `Request failed (${res.status})`);
      const extra = body.unallocated > 0.005
        ? ` ${fmtMoney(body.unallocated)} is unapplied credit.`
        : '';
      onSaved(`Payout of ${fmtMoney(amt)} recorded against `
              + `${selected.length} invoice${selected.length === 1 ? '' : 's'}.${extra}`);
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record payout — ${affiliate?.affiliate?.name || ''}`}
      subtitle="Tick the invoices this payment covers."
      width={620}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || accountLoading || !!accountError}>
            {saving ? <Working label="Recording…" /> : 'Record payout'}
          </PrimaryButton>
        </>
      )}
    >
      {err && <Banner tone="red">{err}</Banner>}
      {accountLoading && <Banner tone="blue"><Working label="Updating account details before recording a payout…" /></Banner>}
      {accountError && <Banner tone="red">Reload the account details before recording a payout. <button type="button" onClick={onReload}>Reload account</button></Banner>}
      <div style={{
        background: C.bg, borderRadius: 10, padding: '12px 14px', marginBottom: 14,
        fontSize: 13, color: C.inkSoft, display: 'flex',
        justifyContent: 'space-between', gap: 10,
      }}>
        <span>
          Owed in total: <strong style={{ color: C.ink }}>{fmtMoney(owed)}</strong>
        </span>
        <span>
          Ticked: <strong style={{ color: C.ink }}>{fmtMoney(selectedTotal)}</strong>
          {' '}({selected.length} of {unsettled.length})
        </span>
      </div>

      {unsettled.length === 0 ? (
        <Banner tone="amber">
          Every commission earned so far has already been paid out. Anything you
          record now is held as credit against future commissions.
        </Banner>
      ) : (
        <div style={{
          border: `1px solid ${C.line}`, borderRadius: 10, marginBottom: 14,
          maxHeight: 250, overflowY: 'auto',
        }}>
          {byBuilding.map(([name, items]) => {
            const allOn = items.every((e) => picked[e.id]);
            return (
              <div key={name}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 12px', background: C.bg,
                  borderBottom: `1px solid ${C.lineSoft}`,
                  fontSize: 12, fontWeight: 700, color: C.inkSoft,
                }}>
                  <input type="checkbox" checked={allOn}
                    onChange={() => toggleBuilding(items, !allOn)} />
                  <FaBuilding size={10} style={{ color: C.muted }} />
                  {name}
                </div>
                {items.map((e) => (
                  <label
                    key={e.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', cursor: 'pointer',
                      borderBottom: `1px solid ${C.lineSoft}`,
                      background: picked[e.id] ? C.card : C.bg,
                    }}
                  >
                    <input type="checkbox" checked={!!picked[e.id]}
                      onChange={() => toggle(e.id)} />
                    <span style={{ fontSize: 12.5, minWidth: 78 }}>
                      <InvoiceLink number={e.invoiceNumber} url={e.jobberUrl} />
                    </span>
                    <span style={{ fontSize: 11.5, color: C.muted, flex: 1 }}>
                      corp paid {fmtDate(e.paymentDate)} · {fmtMoney(e.earnedBase)} @{' '}
                      <button
                        type="button"
                        onClick={(ev) => { ev.preventDefault(); onEditDeal(e); }}
                        title={e.override
                          ? `Custom deal${e.override.note ? `: ${e.override.note}` : ''} — click to change`
                          : 'Click to set a custom deal for this invoice'}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          cursor: 'pointer', fontFamily: sharedFont, fontSize: 11.5,
                          color: e.override ? C.amber : C.muted,
                          fontWeight: e.override ? 700 : 400,
                          textDecoration: 'underline', textDecorationStyle: 'dotted',
                        }}
                      >
                        {e.percent}%{e.override ? '*' : ''}
                      </button>
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      {fmtMoney(e.outstanding)}
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {clawbacks.length > 0 && (
        <Banner tone="amber">
          {clawbacks.length} refund/reversal totalling {fmtMoney(Math.abs(clawbackTotal))}
          {' '}is netted off automatically and can't be excluded.
        </Banner>
      )}
      {short > 0.005 && (
        <Banner tone="red">
          The ticked invoices come to {fmtMoney(selectedTotal)}, which is more
          than the {fmtMoney(amt)} you're recording. Untick some or raise the amount.
        </Banner>
      )}
      {over > 0.005 && (
        <Banner tone="amber">
          {fmtMoney(over)} more than the ticked invoices. The extra is held as
          unapplied credit and will settle against future commissions.
        </Banner>
      )}
      <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Amount">
          <input style={inputStyle} value={amount} inputMode="decimal"
            onChange={(e) => { setAmountEdited(true); setAmount(e.target.value); }} />
        </Field>
        <Field label="Date paid">
          <input style={inputStyle} type="date" value={paidDate}
            onChange={(e) => setPaidDate(e.target.value)} />
        </Field>
      </div>
      <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Method">
          <select style={inputStyle} value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">—</option>
            <option value="Cash">Cash</option>
            <option value="E-transfer">E-transfer</option>
            <option value="Cheque">Cheque</option>
            <option value="Direct deposit">Direct deposit</option>
            <option value="Other">Other</option>
          </select>
        </Field>
        <Field label="Reference" hint="Cheque number, transfer ID, etc.">
          <input style={inputStyle} value={reference}
            onChange={(e) => setReference(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ---------- detail panel --------------------------------------------------

const LEDGER_COLS = {
  invoice: { get: (e) => Number(e.invoiceNumber) || 0, dir: 'desc' },
  building: { get: (e) => (e.buildingName || '').toLowerCase(), dir: 'asc' },
  paid: { get: (e) => e.paymentDate || '', dir: 'desc' },
  base: { get: (e) => e.earnedBase || 0, dir: 'desc' },
  rate: { get: (e) => e.percent || 0, dir: 'desc' },
  amount: { get: (e) => e.amount || 0, dir: 'desc' },
  // Settled rows read as 0 here, so sorting by this floats whatever still
  // needs paying to the top — which is the reason to open this tab at all.
  outstanding: { get: (e) => Math.abs(e.outstanding) || 0, dir: 'desc' },
};

function DetailPanel({ detail, selected, loading, error, pipelineState, onRetry, onRetryPipeline,
  buildings, buildingsLoading, buildingsError, onLoadBuildings, onClose, onChanged, notify }) {
  const [tab, setTab] = useState('buildings');
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [exporting, setExporting] = useState(false);
  const [startDateFor, setStartDateFor] = useState(null);
  const [dealFor, setDealFor] = useState(null);

  const a = detail?.affiliate || selected;
  const t = detail?.totals || {};
  const readOnly = loading || !!error;
  const {
    sorted: ledgerRows, sort: ledgerSort, toggle: toggleLedger,
  } = useSort(detail?.earnings || [], LEDGER_COLS, 'paid');

  const endAgreement = async (agreementId, name) => {
    if (!window.confirm(
      `Stop commissions on ${name}?\n\nAnything already earned stays owed — this only stops future accrual.`,
    )) return;
    setBusy(agreementId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/agreements/${agreementId}/end`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      notify(`${name} detached.`);
      onChanged();
    } catch (e) {
      notify(e.message || 'Could not end the agreement.');
    } finally {
      setBusy('');
    }
  };

  const deletePayout = async (payoutId, amount) => {
    if (!window.confirm(
      `Delete the ${fmtMoney(amount)} payout?\n\nThe commissions it settled go back to being owed.`,
    )) return;
    setBusy(payoutId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/payouts/${payoutId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      notify('Payout deleted.');
      onChanged();
    } catch (e) {
      notify(e.message || 'Could not delete the payout.');
    } finally {
      setBusy('');
    }
  };

  // The endpoint needs an auth header, so it can't be a plain download link —
  // fetch it and hand the browser a blob instead.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/affiliates/${a.id}/statement.csv`,
        { headers: authHeaders() },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(a.name || 'affiliate').replace(/[^\w-]+/g, '-')}`
        + `-statement-${todayIso()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify('Statement downloaded.');
    } catch (e) {
      notify(e.message || 'Could not build the report.');
    } finally {
      setExporting(false);
    }
  };

  const tabs = [
    ['buildings', `Buildings (${(detail?.agreements || []).filter((x) => x.current).length})`],
    ['ledger', `Commissions (${(detail?.earnings || []).length})`],
    ['payouts', `Payments (${(detail?.payouts || []).length})`],
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={a.name}
      subtitle={[a.type, a.company, a.email, a.phone].filter(Boolean).join(' · ') || 'No contact details'}
      width={940}
    >
      {loading && <div role="status" style={{ padding: '10px 0', color: C.muted }}><Working label={detail ? 'Refreshing account…' : 'Loading account…'} /></div>}
      {error && <Banner tone="red">{error} <button type="button" onClick={onRetry}>Try again</button></Banner>}
      {detail && <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Pill
          label="Owed now"
          value={fmtMoney(t.owed)}
          sub={pipelineState === 'ready'
            ? (t.pipeline > 0 ? `${fmtMoney(t.pipeline)} once sent invoices are paid` : 'No pending invoices')
            : pipelineState === 'error' ? 'Pending invoices unavailable' : 'Checking pending invoices…'}
          tone={t.owed > 0 ? 'green' : 'navy'}
        />
        <Pill label="Earned all time" value={fmtMoney(t.earned)} />
        <Pill label="Paid all time" value={fmtMoney(t.paid)} />
      </div>

      {pipelineState === 'error' && <Banner>Pending invoice amounts could not be loaded. Saved commissions and payments are shown. <button type="button" onClick={onRetryPipeline}>Retry pending invoices</button></Banner>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <PrimaryButton onClick={() => setPayoutOpen(true)} disabled={readOnly || !(t.owed > 0)}>
          <FaCheck /> Record payout
        </PrimaryButton>
        <SecondaryButton onClick={() => setAgreementOpen(true)} disabled={readOnly}>
          <FaPlus /> Attach building
        </SecondaryButton>
        <SecondaryButton onClick={exportCsv} disabled={exporting}>
          {exporting ? <Working label="Building report…" />
            : <><FaFileCsv /> Export statement</>}
        </SecondaryButton>
      </div>

      <div style={{
        display: 'flex', gap: 4, borderBottom: `1px solid ${C.line}`, marginBottom: 4,
      }}>
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '9px 13px', fontSize: 13, fontWeight: 600,
              fontFamily: sharedFont,
              color: tab === key ? C.navy : C.muted,
              borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {tab === 'buildings' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Building</th>
                <th style={th}>Rate</th>
                <th style={th}>Since</th>
                <th style={{ ...th, textAlign: 'right' }}>Earned</th>
                <th style={{ ...th, textAlign: 'right' }}>Owed</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {(detail.agreements || []).filter((g) => g.current).map((g) => (
                <BuildingRow
                  key={g.id}
                  agreement={g}
                  rollup={(detail.buildings || []).find((b) => b.buildingId === g.buildingId)}
                  onDetach={() => endAgreement(g.id, g.buildingName)}
                  onEditStart={() => !readOnly && setStartDateFor(g)}
                  onEditDeal={(inv) => !readOnly && setDealFor({ ...inv, buildingId: g.buildingId })}
                  busy={readOnly || busy === g.id}
                />
              ))}
              {(detail.agreements || []).filter((g) => g.current).length === 0 && (
                <tr><td style={{ ...td, color: C.muted }} colSpan={6}>
                  No buildings attached yet.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'buildings' && (detail.agreements || []).some((g) => !g.current) && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: C.muted,
              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
            }}>
              Rate history
            </div>
            {(detail.agreements || []).filter((g) => !g.current).map((g) => (
              <div key={g.id} style={{ fontSize: 12.5, color: C.muted, padding: '3px 0' }}>
                {g.buildingName} — {g.percent}% from {fmtDate(g.startDate)} to {fmtDate(g.endDate)}
              </div>
            ))}
          </div>
        )}

        {tab === 'ledger' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <SortHeader label="Invoice" sortKey="invoice"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Building" sortKey="building"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Paid on" sortKey="paid"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Base" sortKey="base" align="right"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Rate" sortKey="rate" align="right"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Commission" sortKey="amount" align="right"
                  sort={ledgerSort} onSort={toggleLedger} />
                <SortHeader label="Outstanding" sortKey="outstanding" align="right"
                  sort={ledgerSort} onSort={toggleLedger} />
              </tr>
            </thead>
            <tbody>
              {ledgerRows.map((e) => {
                // Nothing left outstanding means we've already paid them for
                // this invoice, so it's history rather than something to act on.
                const settled = Math.abs(e.outstanding) < 0.005;
                return (
                <tr key={e.id} style={{ opacity: settled ? 0.5 : 1 }}>
                  <td style={td}>
                    <InvoiceLink number={e.invoiceNumber} url={e.jobberUrl} />
                    {e.kind === 'clawback' && (
                      <span style={{
                        marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: C.red,
                        background: C.redSoft, padding: '2px 6px', borderRadius: 5,
                      }}>
                        {e.adjustmentType || 'REVERSAL'}
                      </span>
                    )}
                    {e.kind === 'adjustment' && (
                      <span style={{
                        marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: C.amber,
                        background: C.amberSoft, padding: '2px 6px', borderRadius: 5,
                      }}>
                        CUSTOM DEAL
                      </span>
                    )}
                    {settled && (
                      <span style={{
                        marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: C.green,
                        background: C.greenSoft, padding: '1px 5px', borderRadius: 4,
                      }}>
                        <FaCheck size={7} /> PAID OUT
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 12.5 }}>{e.buildingName}</td>
                  <td style={{ ...td, color: C.muted, fontSize: 12.5 }}>{fmtDate(e.paymentDate)}</td>
                  <td style={{ ...td, textAlign: 'right', fontSize: 12.5 }}>
                    {fmtMoney(e.earnedBase)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontSize: 12.5 }}>
                    {e.kind === 'adjustment' ? '—' : (
                      <button
                        onClick={() => !readOnly && setDealFor(e)}
                        title={e.override
                          ? `Custom deal${e.override.note ? `: ${e.override.note}` : ''} — click to change`
                          : 'Click to set a custom deal for this invoice'}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          cursor: 'pointer', fontFamily: sharedFont, fontSize: 12.5,
                          color: e.override ? C.amber : C.ink,
                          fontWeight: e.override ? 700 : 400,
                          textDecoration: 'underline', textDecorationStyle: 'dotted',
                        }}
                      >
                        {e.percent}%{e.override ? '*' : ''}
                      </button>
                    )}
                  </td>
                  <td style={{
                    ...td, textAlign: 'right', fontWeight: 600,
                    color: e.amount < 0 ? C.red : C.ink,
                  }}>
                    {fmtMoney(e.amount)}
                  </td>
                  <td style={{
                    ...td, textAlign: 'right',
                    color: settled ? C.muted : C.green,
                    fontWeight: settled ? 400 : 700,
                  }}>
                    {settled ? '—' : fmtMoney(e.outstanding)}
                  </td>
                </tr>
                );
              })}
              {(detail.earnings || []).length === 0 && (
                <tr><td style={{ ...td, color: C.muted }} colSpan={7}>
                  Nothing yet. Commissions appear here once a corp invoice for an
                  attached building gets paid — run Sync to pull the latest.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'payouts' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={th}>Method</th>
                <th style={th}>Reference</th>
                <th style={th}>Covers</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {(detail.payouts || []).map((p) => (
                <tr key={p.id}>
                  <td style={td}>{fmtDate(p.paidDate)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                    {fmtMoney(p.amount)}
                  </td>
                  <td style={{ ...td, fontSize: 12.5 }}>{p.method || '—'}</td>
                  <td style={{ ...td, fontSize: 12.5, color: C.muted }}>{p.reference || '—'}</td>
                  <td style={{ ...td, fontSize: 12, color: C.muted }}>
                    {(p.covers || []).length ? (
                      <>
                        {p.covers.length} invoice{p.covers.length === 1 ? '' : 's'}:{' '}
                        {p.covers.slice(0, 3).map((c, n) => (
                          <React.Fragment key={c.invoiceNumber}>
                            {n > 0 && ', '}
                            <InvoiceLink number={c.invoiceNumber} url={c.jobberUrl}
                              style={{ fontSize: 12 }} />
                          </React.Fragment>
                        ))}
                        {p.covers.length > 3 && ` +${p.covers.length - 3} more`}
                      </>
                    ) : 'Unapplied credit'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button
                      onClick={() => deletePayout(p.id, p.amount)}
                      disabled={readOnly || busy === p.id}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: C.red, fontSize: 12,
                      }}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
              {(detail.payouts || []).length === 0 && (
                <tr><td style={{ ...td, color: C.muted }} colSpan={6}>
                  No payments recorded yet.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <AgreementModal
        open={agreementOpen}
        affiliate={detail}
        buildings={buildings}
        buildingsLoading={buildingsLoading}
        buildingsError={buildingsError}
        onLoadBuildings={onLoadBuildings}
        onClose={() => setAgreementOpen(false)}
        onSaved={(msg) => { notify(msg); onChanged(); }}
      />
      <PayoutModal
        open={payoutOpen}
        affiliate={detail}
        accountLoading={loading}
        accountError={error}
        onReload={onChanged}
        onClose={() => setPayoutOpen(false)}
        onSaved={(msg) => { notify(msg); onChanged(); }}
        onEditDeal={(inv) => !readOnly && setDealFor(inv)}
      />
      <DealModal
        open={!!dealFor}
        affiliateId={detail?.affiliate?.id}
        invoice={dealFor}
        onClose={() => setDealFor(null)}
        onSaved={(msg) => { notify(msg); onChanged(); }}
      />

      <StartDateModal
        open={!!startDateFor}
        agreement={startDateFor}
        onClose={() => setStartDateFor(null)}
        onSaved={(msg) => { notify(msg); onChanged(); }}
      />
      </>}
    </Modal>
  );
}

// ---------- page ----------------------------------------------------------

export default function MaintenanceAffiliatePayouts() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [buildingsError, setBuildingsError] = useState('');
  const [types, setTypes] = useState([]);
  const [syncStatus, setSyncStatus] = useState({});
  const [dataWarnings, setDataWarnings] = useState([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pipelineState, setPipelineState] = useState('loading');
  const [resetOpen, setResetOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'owed', dir: 'desc' });
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [affiliateModal, setAffiliateModal] = useState({ open: false, editing: null });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailPipelineState, setDetailPipelineState] = useState('loading');
  const selectedId = useRef(null);
  const requests = useRef({});
  const buildingsLoadedAt = useRef(0);
  const toastTimer = useRef(null);

  // Independent request lanes prevent an old selection or pre-mutation result
  // from replacing newer data, even if a server finishes after cancellation.
  const beginRequest = useCallback((key) => {
    requests.current[key]?.abort();
    const controller = new AbortController();
    requests.current[key] = controller;
    return controller;
  }, []);
  const isCurrent = useCallback((key, controller) => (
    requests.current[key] === controller && !controller.signal.aborted
  ), []);

  useEffect(() => () => {
    Object.values(requests.current).forEach((controller) => controller.abort());
    clearTimeout(toastTimer.current);
  }, []);

  const notify = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }, []);

  const loadPipeline = useCallback(async () => {
    const controller = beginRequest('pipeline');
    setPipelineState('loading');
    try {
      const body = await readAffiliateForecast(`${API_BASE_URL}/api/affiliates/pipeline`, authHeaders(), controller.signal);
      if (!isCurrent('pipeline', controller)) return;
      if (!Array.isArray(body.affiliates)) throw new Error('Pending invoice amounts could not be read.');
      const forecasts = new Map(body.affiliates.map((row) => [row.id, row]));
      setRows((current) => current.map((row) => ({
        ...row,
        pipelineAmount: forecasts.get(row.id)?.pipelineAmount || 0,
        pipelineInvoices: forecasts.get(row.id)?.pipelineInvoices || 0,
      })));
      setTotals((current) => ({ ...current, pipeline: body.total }));
      setPipelineState('ready');
    } catch (e) {
      if (isCurrent('pipeline', controller)) setPipelineState('error');
    }
  }, [beginRequest, isCurrent]);

  const load = useCallback(async () => {
    const controller = beginRequest('list');
    requests.current.pipeline?.abort();
    setLoading(true);
    setPipelineState('loading');
    setError('');
    try {
      const list = await readAffiliateJson(`${API_BASE_URL}/api/affiliates?pipeline=0`, authHeaders(), controller.signal);
      if (!isCurrent('list', controller)) return;
      if (!Array.isArray(list.affiliates) || !list.totals) throw new Error('The affiliate data could not be read. Please try again.');
      setRows(list.affiliates);
      setTotals(list.totals || {});
      setTypes(list.types || []);
      setSyncStatus(list.syncStatus || {});
      setDataWarnings(list.dataWarnings || []);
      setHasLoaded(true);
      // Saved balances render first. Forecasts never block the table.
      loadPipeline();
    } catch (e) {
      if (!isCurrent('list', controller)) return;
      setError(e.message || 'Could not load affiliates.');
      setPipelineState('error');
      if (e.status === 403 || e.status === 401) {
        setRows([]);
        setTotals({});
        setHasLoaded(false);
      }
    } finally {
      if (isCurrent('list', controller)) setLoading(false);
    }
  }, [beginRequest, isCurrent, loadPipeline]);

  useEffect(() => { load(); }, [load]);

  const loadBuildings = useCallback(async (force = false) => {
    if (!force && Date.now() - buildingsLoadedAt.current < 5 * 60 * 1000) return;
    if (!force && requests.current.buildings && !requests.current.buildings.signal.aborted) return;
    const controller = beginRequest('buildings');
    setBuildingsLoading(true);
    setBuildingsError('');
    try {
      const body = await readAffiliateJson(`${API_BASE_URL}/api/affiliates/attachable-buildings`, authHeaders(), controller.signal);
      if (!isCurrent('buildings', controller)) return;
      if (!Array.isArray(body.buildings)) throw new Error('The building list could not be read. Please try again.');
      setBuildings(body.buildings);
      buildingsLoadedAt.current = Date.now();
    } catch (e) {
      if (isCurrent('buildings', controller)) setBuildingsError(e.message || 'Could not load buildings.');
    } finally {
      if (isCurrent('buildings', controller)) {
        setBuildingsLoading(false);
        delete requests.current.buildings;
      }
    }
  }, [beginRequest, isCurrent]);

  const loadDetailPipeline = useCallback(async (id) => {
    const controller = beginRequest('detailPipeline');
    setDetailPipelineState('loading');
    try {
      const body = await readAffiliateForecast(`${API_BASE_URL}/api/affiliates/${id}/pipeline`, authHeaders(), controller.signal);
      if (!isCurrent('detailPipeline', controller) || selectedId.current !== id) return;
      if (body.affiliateId !== id || !Array.isArray(body.rows)) throw new Error('Pending invoice amounts could not be read.');
      setDetail((current) => current?.affiliate?.id === id ? mergeAffiliatePipeline(current, body) : current);
      setDetailPipelineState('ready');
    } catch (e) {
      if (isCurrent('detailPipeline', controller) && selectedId.current === id) setDetailPipelineState('error');
    }
  }, [beginRequest, isCurrent]);

  const readDetail = useCallback(async (id) => {
    const controller = beginRequest('detail');
    requests.current.detailPipeline?.abort();
    setDetailLoading(true);
    setDetailError('');
    setDetailPipelineState('loading');
    try {
      const body = await readAffiliateJson(`${API_BASE_URL}/api/affiliates/${id}?pipeline=0`, authHeaders(), controller.signal);
      if (!isCurrent('detail', controller) || selectedId.current !== id) return;
      if (body.affiliate?.id !== id || !body.totals) throw new Error('The account data could not be read. Please try again.');
      setDetail(body);
      loadDetailPipeline(id);
    } catch (e) {
      if (!isCurrent('detail', controller) || selectedId.current !== id) return;
      setDetailError(e.message || 'Could not open that affiliate.');
      if (e.status === 403 || e.status === 401) setDetail(null);
    } finally {
      if (isCurrent('detail', controller) && selectedId.current === id) setDetailLoading(false);
    }
  }, [beginRequest, isCurrent, loadDetailPipeline]);

  const openDetail = useCallback((row) => {
    selectedId.current = row.id;
    setSelected(row);
    setDetail(null);
    readDetail(row.id);
  }, [readDetail]);

  const closeDetail = useCallback(() => {
    selectedId.current = null;
    requests.current.detail?.abort();
    requests.current.detailPipeline?.abort();
    setSelected(null);
    setDetail(null);
  }, []);

  const refreshDetail = useCallback(async () => {
    buildingsLoadedAt.current = 0;
    await Promise.all([
      load(),
      selectedId.current ? readDetail(selectedId.current) : Promise.resolve(),
    ]);
  }, [load, readDetail]);

  // A normal sync only asks Jobber for invoices that changed since the last
  // run, per building. `full` re-reads everything back to the earliest
  // agreement start — slower, and only needed if a run was interrupted.
  const runSync = useCallback(async (full = false) => {
    if (full && !window.confirm(
      'Re-read every invoice back to the earliest agreement start?\n\n'
      + 'Only needed if a sync was interrupted. Slower, but it cannot '
      + 'double-count.',
    )) return;
    setSyncing(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/affiliates/sync${full ? '?full=1' : ''}`,
        { method: 'POST', headers: authHeaders() },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      notify(
          body.created
            ? `${body.created} new commission${body.created === 1 ? '' : 's'} found across ${body.invoicesScanned} invoices.`
            : `Up to date — checked ${body.invoicesScanned} invoice${body.invoicesScanned === 1 ? '' : 's'}, nothing new.`,
      );
      await refreshDetail();
    } catch (e) {
      notify(e.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }, [refreshDetail, notify]);

  // Reading the page never starts a ledger-writing Jobber sync. Staff can
  // explicitly sync when needed, without every browser repeating that work.

  // Deletes the arithmetically impossible rows and re-syncs them. Separate
  // from "full re-sync" because that dedupes on the ledger key and so leaves
  // a wrong row exactly as it found it.
  const rebuildOverstated = useCallback(async () => {
    if (!window.confirm(
      'Recalculate the overstated commissions?\n\n'
      + 'The affected rows are deleted and pulled from Jobber again. Payout '
      + 'history is untouched.',
    )) return;
    setRebuilding(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/affiliates/rebuild-overstated`,
        { method: 'POST', headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      notify(body.deleted
        ? `Rebuilt ${body.deleted} commission${body.deleted === 1 ? '' : 's'}.`
        : 'Nothing needed rebuilding.');
      await refreshDetail();
    } catch (e) {
      notify(e.message || 'Could not rebuild those commissions.');
    } finally {
      setRebuilding(false);
    }
  }, [refreshDetail, notify]);

  const unlinked = useMemo(
    () => buildings.filter((b) => !b.linked).length,
    [buildings],
  );
  const dupes = useMemo(
    () => buildings.filter((b) => b.duplicateOf).length,
    [buildings],
  );

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const pick = {
      name: (r) => (r.name || '').toLowerCase(),
      buildings: (r) => r.buildings.length,
      owed: (r) => r.owed,
      earned: (r) => r.earned,
      paid: (r) => r.paid,
      // Never-paid sorts as the oldest possible date rather than being dropped,
      // so "who's overdue a payment" puts them first.
      lastPaid: (r) => r.lastPaidDate || '',
    }[key] || ((r) => r.owed);
    const mult = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = pick(a);
      const y = pick(b);
      if (x < y) return -1 * mult;
      if (x > y) return 1 * mult;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [rows, sort]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key === key
      // Text reads naturally A-Z first; money and dates are more useful
      // largest/latest first, so each column starts the way you'd expect.
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  }, []);

  return (
    <div className="internal-secondary-page internal-affiliate-page" style={{ padding: '28px 30px', fontFamily: sharedFont, background: C.bg, minHeight: '100vh' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: C.ink, fontWeight: 700 }}>
            Affiliate Marketing
          </h1>
          <div style={{ marginTop: 5, fontSize: 13, color: C.muted, maxWidth: 640, lineHeight: 1.5 }}>
            Commissions on work billed to the condo corporation, calculated on the
            pre-HST subtotal of invoices that have actually been paid. Resident
            in-suite work is not included.
          </div>
        </div>
        <div className="affiliate-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <SecondaryButton onClick={refreshDetail} disabled={loading || syncing}>
            <FaSyncAlt /> {loading && hasLoaded ? 'Refreshing…' : 'Refresh'}
          </SecondaryButton>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <SecondaryButton onClick={() => runSync(false)} disabled={syncing}>
              <FaSyncAlt style={syncing ? { animation: 'spin 1s linear infinite' } : undefined} />
              {syncing ? 'Syncing…' : 'Sync from Jobber'}
            </SecondaryButton>
            <button
              onClick={() => runSync(true)}
              disabled={syncing}
              title={'Re-reads every invoice back to the earliest agreement start, '
                + 'ignoring the "changed since last sync" shortcut. Use it if a sync '
                + 'was interrupted or a figure looks wrong. It cannot double-count.'}
              style={{
                background: 'transparent', border: 'none', color: C.muted,
                fontSize: 11, cursor: syncing ? 'not-allowed' : 'pointer',
                fontFamily: sharedFont, textDecoration: 'underline',
              }}
            >
              full re-sync
            </button>
          </div>
          <PrimaryButton onClick={() => setAffiliateModal({ open: true, editing: null })}>
            <FaPlus /> Add affiliate
          </PrimaryButton>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <Pill label="Owed now" value={hasLoaded ? fmtMoney(totals.owed) : '—'} tone="green"
          sub="Ready to pay out" />
        <Pill label="Will be owed" value={pipelineState === 'ready' ? fmtMoney(totals.pipeline) : '—'} tone="amber"
          sub={pipelineState === 'ready' ? 'Once sent invoices are paid' : pipelineState === 'error' ? 'Pending invoices unavailable' : <Working label="Checking pending invoices…" />} />
        <Pill label="Paid all time" value={hasLoaded ? fmtMoney(totals.paidAllTime) : '—'} />
        <Pill label="Earned all time" value={hasLoaded ? fmtMoney(totals.earnedAllTime) : '—'} />
      </div>

      {error && <Banner tone="red">{error} <button type="button" onClick={load}>Try again</button></Banner>}
      {!error && hasLoaded && pipelineState === 'error' && <Banner>Pending invoice amounts could not be loaded. Saved commissions and payments are shown. <button type="button" onClick={loadPipeline}>Retry pending invoices</button></Banner>}
      {/* Red, not amber: every figure on this page is too high while these
          rows exist, so it's wrong output rather than missing input. */}
      {!error && !loading && dataWarnings.map((w) => (
        <Banner tone="red" key={w.kind}>
          {rebuilding ? (
            <Working label="Recalculating those commissions from Jobber…" />
          ) : (
            <>
              <strong>
                {w.count} commission{w.count === 1 ? '' : 's'} {w.count === 1 ? 'is' : 'are'} overstated
                {w.overstatedBy > 0 && <> by about {fmtMoney(w.overstatedBy)}</>}.
              </strong>{' '}
              {w.count === 1 ? 'It was' : 'They were'} calculated from a payment
              that settled more than one invoice, so the full payment was
              counted against each — affecting invoice
              {w.invoices.length === 1 ? ' ' : 's '}
              {w.invoices.map((n) => `#${n}`).join(', ')}
              {w.affiliates.length > 0 && <> for {w.affiliates.join(', ')}</>}.
              {' '}
              <button
                onClick={rebuildOverstated}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: 'inherit', font: 'inherit', fontWeight: 700,
                  textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                Recalculate them
              </button>
              {' '}— a normal sync won't, since it leaves rows it has already written.
            </>
          )}
        </Banner>
      ))}
      {/* Missing ledger syncs remain explicit; opening the page is read-only. */}
      {!error && !loading && syncStatus.neverSynced > 0 && (
        <Banner tone="amber">
          {syncing ? (
            <Working label={
              `Pulling commissions from Jobber for ${syncStatus.neverSynced} `
              + `newly attached building${syncStatus.neverSynced === 1 ? '' : 's'}…`
            } />
          ) : (
            <>
              {syncStatus.neverSynced === syncStatus.attachedBuildings
                ? 'No commissions have been pulled from Jobber yet'
                : `${syncStatus.neverSynced} of ${syncStatus.attachedBuildings} attached buildings have never been synced`}
              , so "owed now" will read $0 even where invoices have already been
              paid. Press Sync from Jobber to pull the latest commissions.
            </>
          )}
        </Banner>
      )}
      <div className="internal-data-scroll" role="region" aria-label="Affiliate accounts" tabIndex={0} style={{
        background: C.card, borderRadius: 12, boxShadow: C.shadow,
        border: `1px solid ${C.line}`, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortHeader label="Affiliate" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortHeader label="Buildings" sortKey="buildings" sort={sort} onSort={toggleSort} />
              <SortHeader label="Owed now" sortKey="owed" sort={sort} onSort={toggleSort} align="right" />
              <SortHeader label="Earned" sortKey="earned" sort={sort} onSort={toggleSort} align="right" />
              <SortHeader label="Paid" sortKey="paid" sort={sort} onSort={toggleSort} align="right" />
              <SortHeader label="Last paid" sortKey="lastPaid" sort={sort} onSort={toggleSort} />
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {loading && !hasLoaded && (
              <tr><td style={{ ...td, color: C.muted }} colSpan={7}>
                <Working label="Loading affiliates…" />
              </td></tr>
            )}
            {!loading && sorted.length === 0 && !error && (
              <tr><td style={{ ...td, color: C.muted, padding: 28, textAlign: 'center' }} colSpan={7}>
                <FaUserTie size={22} style={{ color: C.line, display: 'block', margin: '0 auto 10px' }} />
                No affiliates yet. Add one, attach the buildings they referred,
                then run Sync to pull in what they've earned.
              </td></tr>
            )}
            {hasLoaded && sorted.map((r) => (
              <tr
                key={r.id}
                onClick={() => openDetail(r)}
                style={{ cursor: 'pointer' }}
              >
                <td style={td}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); openDetail(r); }} style={{ background: 'none', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
                      {r.name}
                    </button>
                  </div>
                  {r.type && (
                    <span style={{
                      display: 'inline-block', fontSize: 10.5, fontWeight: 700,
                      color: C.navy, background: C.blueSoft, borderRadius: 5,
                      padding: '2px 6px', marginTop: 2,
                    }}>
                      {r.type}
                    </span>
                  )}
                  {r.company && (
                    <div style={{ fontSize: 12, color: C.muted }}>{r.company}</div>
                  )}
                  {r.status === 'inactive' && (
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, color: C.muted,
                      background: C.lineSoft, padding: '2px 6px', borderRadius: 5,
                    }}>
                      INACTIVE
                    </span>
                  )}
                </td>
                <td style={td}>
                  {r.buildings.length === 0 && (
                    <span style={{ color: C.muted, fontSize: 12.5 }}>None attached</span>
                  )}
                  {r.buildings.slice(0, 3).map((b) => (
                    <span key={b.buildingId} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: C.blueSoft, color: C.navy, borderRadius: 6,
                      padding: '3px 8px', fontSize: 11.5, fontWeight: 600,
                      marginRight: 5, marginBottom: 3,
                    }}>
                      <FaBuilding size={9} />
                      {b.buildingName} · {b.percent}%
                    </span>
                  ))}
                  {r.buildings.length > 3 && (
                    <span style={{ fontSize: 11.5, color: C.muted }}>
                      +{r.buildings.length - 3} more
                    </span>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{
                    fontWeight: 700, fontSize: 15,
                    color: r.owed > 0 ? C.green : C.muted,
                  }}>
                    {fmtMoney(r.owed)}
                  </div>
                  {pipelineState === 'ready' && r.pipelineAmount > 0 && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                      +{fmtMoney(r.pipelineAmount)} pending
                    </div>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>
                  {fmtMoney(r.earned)}
                </td>
                <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>
                  {fmtMoney(r.paid)}
                </td>
                <td style={{ ...td, fontSize: 12.5, color: C.muted }}>
                  {r.lastPaidDate ? fmtDate(r.lastPaidDate) : 'Never'}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAffiliateModal({ open: true, editing: r });
                    }}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: C.navy, fontSize: 12, fontWeight: 600, marginRight: 8,
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openDetail(r); }}
                    aria-label={`View ${r.name} details`}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: C.muted, fontSize: 12,
                    }}
                  >
                    <FaHistory />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!error && unlinked > 0 && (
        <div style={{ marginTop: 18 }}>
          <Banner tone="amber">
            {unlinked} of {buildings.length} buildings aren't mapped to a Jobber
            client, so commission can't be calculated for them — they're only a
            problem if you try to attach one.
            {dupes > 0 && (
              <>
                {' '}
                <strong>
                  {dupes} of those {dupes === 1 ? 'is a duplicate' : 'are duplicates'} of
                  a building that <em>is</em> mapped
                </strong>
                , which is the case worth watching: picking the wrong copy looks
                right and earns nothing. The picker flags them.
              </>
            )}
          </Banner>
        </div>
      )}

      <div style={{
        marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.line}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <button
          onClick={() => setResetOpen(true)}
          style={{
            background: 'transparent', border: `1px solid ${C.redSoft}`,
            color: C.red, borderRadius: 7, padding: '5px 10px', fontSize: 11.5,
            fontWeight: 600, fontFamily: sharedFont, cursor: 'pointer',
          }}
        >
          <FaTrash size={9} /> Reset all data
        </button>
        <span style={{ fontSize: 11.5, color: C.muted }}>
          Wipes affiliates, agreements, earnings and payouts so you can start a
          test run over. Buildings and Jobber aren't touched.
        </span>
      </div>

      <ResetModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onDone={(msg) => { notify(msg); closeDetail(); load(); }}
      />

      <AffiliateModal
        open={affiliateModal.open}
        editing={affiliateModal.editing}
        types={types}
        onClose={() => setAffiliateModal({ open: false, editing: null })}
        onSaved={(msg) => { notify(msg); refreshDetail(); }}
      />

      {selected && (
        <DetailPanel
          key={selected.id}
          selected={selected}
          detail={detail}
          loading={detailLoading}
          error={detailError}
          pipelineState={detailPipelineState}
          onRetry={() => readDetail(selected.id)}
          onRetryPipeline={() => loadDetailPipeline(selected.id)}
          buildings={buildings}
          buildingsLoading={buildingsLoading}
          buildingsError={buildingsError}
          onLoadBuildings={loadBuildings}
          onClose={closeDetail}
          onChanged={refreshDetail}
          notify={notify}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: C.ink, color: '#fff', padding: '11px 20px', borderRadius: 10,
          fontSize: 13.5, zIndex: 1200, boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          maxWidth: 560, textAlign: 'center',
        }}>
          {toast}
        </div>
      )}

      <style>{'@keyframes spin { from { transform: rotate(0) } to { transform: rotate(360deg) } }'}</style>
    </div>
  );
}
